import { useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { hasValue } from 'utils';

const actions = {
    FAILED: 'FAILED',
    LOADED: 'LOADED',
    RESET: 'RESET'
};

/**
 * Loads asynchronous data from a promise or a function.
 * @param {*} dataSource An array, a Promise that resolves to an array, or a function that returns either.
 * @param {*} errorCallback Optional function like 'error => {}' that is called if the data source cannot be loaded.
 * @param {*} loadCallback Optional function like 'data => {}' that is called when the data source is loaded successfully.
 * @returns An array containing:
 *  [0]: { data, error, isLoading } - The current state.
 *  [1]: setDataSource(dataSource) - A function that can be used to lazily supply the data source.
 */
function useDataSource(dataSource, errorCallback, loadCallback) {
    const [
        {
            data,
            error,
            isLoading,
            isLoaded,
            promise,
            totalCount
        },
        dispatch
    ] = useReducer(dataSourceReducer, null, initState);
    const setDataSource = useCallback(dataSource => dispatch({ type: actions.RESET, payload: dataSource }), [dispatch]);

    useEffect(() => {
        setDataSource(dataSource);
    }, [dataSource, setDataSource]);

    useEffect(() => {
        if (isLoaded && loadCallback) {
            loadCallback(data)
        }
    }, [isLoaded, data, loadCallback]);

    useEffect(() => {
        if(error) {
            console.error('Error loading data source.', error);
            errorCallback && errorCallback(error);
        }
    }, [error, errorCallback]);

    useEffect(() => {
        if (promise) {
            promise
                .then(result => {
                    dispatch({ type: actions.LOADED, payload: result });
                })
                .catch(error => {
                    dispatch({ type: actions.FAILED, payload: error });
                });
        }
    }, [promise]);

    return [{ data, isLoading, isLoaded, error, totalCount }, setDataSource];
}

function initState(dataSource) {
    if (hasValue(dataSource)) {
        if (Array.isArray(dataSource)) {
            return {
                data: dataSource,
                error: null,
                isLoading: false,
                isLoaded: true,
                promise: null,
                totalCount: dataSource.length,
            };
        }

        return {
            data: null,
            error: null,
            isLoading: true,
            isLoaded: false,
            promise: getAsyncData(dataSource),
            totalCount: 0,
        };
    }

    return {
        data: null,
        error: null,
        isLoading: false,
        isLoaded: false,
        promise: null,
        totalCount: 0,
    };
}

function dataSourceReducer(state, action) {
    switch (action.type) {
        case actions.LOADED:
            return {
                data: action.payload,
                error: null,
                isLoading: false,
                isLoaded: true,
                promise: null,
                totalCount: action.payload ? action.payload.totalCount || action.payload.length || 0 : 0,
            };

        case actions.RESET:
            const dataSource = action.payload;
            return initState(dataSource);

        case actions.FAILED:
            return {
                data: null,
                error: action.payload,
                isLoading: false,
                isLoaded: false,
                promise: null,
                totalCount: 0,
            };

        default:
            return state;
    }
}

async function getAsyncData(dataSource) {
    if (dataSource instanceof Promise) {
        const result = await dataSource;
        return result;
    }

    if (typeof(dataSource) === 'function') {
        return await getAsyncData(dataSource());
    }

    if (dataSource instanceof Array) {
        return dataSource;
    }

    return dataSource;
}

const dataSourceTypes = [
    PropTypes.array,
    PropTypes.instanceOf(Promise),
    PropTypes.func
];

export default useDataSource;
export { dataSourceTypes };